import * as React from "react";
import styled, { css } from "styled-components";

const TableContainer = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  caption-side: bottom;
  font-size: 0.875rem;
  border-collapse: collapse;
`;

const StyledThead = styled.thead`
  & tr {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const StyledTbody = styled.tbody`
  & tr:last-child {
    border-bottom: none;
  }
`;

const StyledTfoot = styled.tfoot`
  background: rgba(243, 244, 246, 0.5);
  border-top: 1px solid #e5e7eb;
  font-weight: 500;
  & > tr:last-child {
    border-bottom: none;
  }
`;

const StyledTr = styled.tr<{ $selected?: boolean }>`
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.15s;

  &:hover {
    background: rgba(243, 244, 246, 0.5);
  }

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f3f4f6;
    `}
`;

const StyledTh = styled.th`
  color: #1a1a1a;
  height: 2.5rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  text-align: left;
  vertical-align: middle;
  font-weight: 500;
  white-space: nowrap;

  &[role="checkbox"] {
    padding-right: 0;
    vertical-align: middle;
  }
  & > [role="checkbox"] {
    transform: translateY(2px);
  }
`;

const StyledTd = styled.td`
  padding: 0.5rem;
  vertical-align: middle;
  white-space: nowrap;

  &[role="checkbox"] {
    padding-right: 0;
    vertical-align: middle;
  }
  & > [role="checkbox"] {
    transform: translateY(2px);
  }
`;

const StyledCaption = styled.caption`
  color: #6b7280;
  margin-top: 1rem;
  font-size: 0.875rem;
`;

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <TableContainer data-slot="table-container">
      <StyledTable data-slot="table" className={className} {...props} />
    </TableContainer>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <StyledThead data-slot="table-header" className={className} {...props} />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <StyledTbody data-slot="table-body" className={className} {...props} />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <StyledTfoot data-slot="table-footer" className={className} {...props} />
  );
}

function TableRow({
  className,
  ...props
}: React.ComponentProps<"tr"> & { "data-state"?: string }) {
  const $selected = props["data-state"] === "selected";
  return (
    <StyledTr
      data-slot="table-row"
      className={className}
      $selected={$selected}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return <StyledTh data-slot="table-head" className={className} {...props} />;
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return <StyledTd data-slot="table-cell" className={className} {...props} />;
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <StyledCaption data-slot="table-caption" className={className} {...props} />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
